/**
 * WizardStepAssets
 *
 * Step 4 of the launchpad creation wizard.
 * Handles NFT asset uploads (folder with images/metadata).
 * Uses per-file uploads instead of ZIP for reliability with large collections.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { FeatherUpload, FeatherExternalLink } from '@subframe/core';
import { Button } from '../../../ui/components/Button';
import { LoadingSpinner } from '../../ui/LoadingSpinner';
import { useAssetUpload } from '../../../hooks/useAssetUpload';

export interface WizardStepAssetsProps {
  launchId: string;
  totalItems: number;
  onUploadComplete: (result: { uploaded: number; totalSupply: number }) => void;
  onError?: (error: string) => void;
}

export const WizardStepAssets: React.FC<WizardStepAssetsProps> = ({
  launchId,
  totalItems,
  onUploadComplete,
  onError,
}) => {
  const {
    progress,
    errors,
    isUploading,
    isDragging,
    uploadFolder,
    cancelUpload,
    retryFailed,
    failedCount,
    dragHandlers,
    inputRef,
  } = useAssetUpload({
    launchId,
    clearBeforeUpload: false,
    onComplete: onUploadComplete,
    onError,
  });

  return (
    <div className="space-y-6">
      {/* Instructions */}
      <div className="p-4 rounded-lg bg-neutral-100 border border-neutral-border">
        <p className="text-body font-body text-default-font mb-2">
          Upload your NFT files. Your folder should contain:
        </p>
        <ul className="list-disc list-inside text-body font-body text-default-font space-y-1">
          <li>
            <code className="font-mono bg-neutral-200 px-1 rounded">images/</code> folder with numbered images (1.png, 2.png, etc.)
          </li>
          <li>
            <code className="font-mono bg-neutral-200 px-1 rounded">metadata/</code> folder with corresponding JSON files (1.json, 2.json, etc.)
          </li>
        </ul>
        <p className="text-body font-body text-default-font mt-3">
          <Link
            to="/launchpad/guide"
            target="_blank"
            className="inline-flex items-center gap-1 text-brand-600 hover:text-brand-700 hover:underline"
          >
            Read the Creator Guide for detailed format requirements
            <FeatherExternalLink className="w-4 h-4" />
          </Link>
        </p>
      </div>

      {/* Current Status */}
      {totalItems > 0 && !isUploading && (
        <div className="p-4 rounded-lg border border-success-600 bg-neutral-100">
          <p className="text-body font-body text-default-font">
            <strong>{totalItems}</strong> NFT{totalItems !== 1 ? 's' : ''} already uploaded.
            Upload again to replace all assets.
          </p>
        </div>
      )}

      {/* Upload Area */}
      <div
        {...dragHandlers}
        className={`relative p-8 rounded-lg border-2 border-dashed transition-colors ${
          isDragging
            ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/20'
            : 'border-neutral-300 dark:border-neutral-600 hover:border-brand-400'
        }`}
      >
        {isUploading ? (
          <div className="flex flex-col items-center gap-4">
            <LoadingSpinner size="md" />
            <p className="text-body font-body text-default-font">
              {progress.statusMessage || 'Uploading...'}
            </p>
            {progress.totalItems > 0 && (
              <>
                <div className="w-full max-w-xs bg-neutral-200 dark:bg-neutral-700 rounded-full h-2">
                  <div
                    className="bg-brand-500 h-2 rounded-full transition-all"
                    style={{ width: `${progress.percent}%` }}
                  />
                </div>
                <p className="text-caption font-caption text-subtext-color">
                  {progress.completedItems} of {progress.totalItems}
                  {progress.failedItems > 0 && ` (${progress.failedItems} failed)`}
                </p>
              </>
            )}
            <Button
              variant="neutral-tertiary"
              size="small"
              onClick={cancelUpload}
            >
              Cancel
            </Button>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-4">
            <FeatherUpload className="w-12 h-12 text-neutral-400" />
            <div className="text-center">
              <p className="text-body font-body text-default-font">
                Drag and drop your NFT folder here
              </p>
              <p className="text-caption font-caption text-subtext-color mt-1">
                Folder should contain{' '}
                <code className="font-mono text-brand-600">images/</code> and{' '}
                <code className="font-mono text-brand-600">metadata/</code> directories
              </p>
            </div>
            {failedCount > 0 && (
              <div className="flex items-center gap-3">
                <p className="text-caption font-caption text-error-600">
                  {failedCount} items failed to upload
                </p>
                <Button
                  variant="brand-secondary"
                  size="small"
                  onClick={retryFailed}
                >
                  Retry Failed
                </Button>
              </div>
            )}
            <div className="flex items-center gap-4">
              <Button
                variant="neutral-secondary"
                onClick={() => inputRef.current?.click()}
              >
                Select Folder
              </Button>
            </div>
            <input
              ref={inputRef}
              type="file"
              // @ts-ignore - webkitdirectory is a non-standard attribute
              webkitdirectory=""
              directory=""
              multiple
              className="hidden"
              onChange={(e) => e.target.files && uploadFolder(e.target.files)}
            />
          </div>
        )}
      </div>

      {/* Upload Errors */}
      {errors.length > 0 && (
        <div className="p-4 bg-warning-100 dark:bg-warning-900/20 border border-warning-200 dark:border-warning-800 rounded-lg">
          <p className="text-body-bold font-body-bold text-warning-700 dark:text-warning-400 mb-2">
            Some items had issues:
          </p>
          <ul className="list-disc list-inside space-y-1">
            {errors.slice(0, 10).map((err, i) => (
              <li key={i} className="text-caption font-caption text-warning-600 dark:text-warning-500">
                {err}
              </li>
            ))}
            {errors.length > 10 && (
              <li className="text-caption font-caption text-warning-600 dark:text-warning-500">
                ...and {errors.length - 10} more
              </li>
            )}
          </ul>
        </div>
      )}

      {/* Skip Info */}
      <p className="text-caption font-caption text-subtext-color text-center">
        You can skip this step and upload assets later from the dashboard.
      </p>
    </div>
  );
};
